import React, { useEffect, useState, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './utils/axiosConfig'; // Import axios configuration
import './App.css';
import './theme.css';
import './styles/accessibility.css';
// Import i18n (needs to be imported before any component that uses translations)
import './i18n';

// Layout Components
import Header from './components/layout/Header';
import FeedbackForm from './components/common/FeedbackForm';

// Landing Page
import LandingPage from './components/pages/LandingPage';

// Organization Setup
import OrganizationSetup from './components/organization/OrganizationSetup';

// Page Components
import Dashboard from './components/pages/Dashboard';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import PaymentSuccess from './components/auth/PaymentSuccess';
import AcceptInvite from './components/auth/AcceptInvite';
import ForcePasswordChange from './components/auth/ForcePasswordChange';
import AppTutorial from './components/tutorial/AppTutorial';
import AssessmentList from './components/assessments/AssessmentList';
import AssessmentDetail from './components/assessments/AssessmentDetail';
import ReportList from './components/reports/ReportList';
import ReportDetail from './components/reports/ReportDetail';
import MaturityScorecardPage from './components/pages/MaturityScorecardPage';
import ScorecardDetailsPage from './components/pages/ScorecardDetailsPage';
import FindingsRecommendationsPage from './components/pages/FindingsRecommendationsPage';
import IndustryAnalysisPage from './components/pages/IndustryAnalysisPage';
import ImplementationRoadmap from './components/pages/ImplementationRoadmap';
import AccountSettings from './components/account/AccountSettings';
import MasterServicesAgreement from './components/legal/MasterServicesAgreement';
import Profile from './components/pages/Profile';
import Settings from './components/pages/Settings';
import AppSettings from './components/pages/AppSettings';
import OrganizationDetails from './components/organization/OrganizationDetails';
import NotFound from './components/pages/NotFound';

// Admin Components
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import TemplateManagement from './components/admin/TemplateManagement';
import OrganizationManagement from './components/admin/OrganizationManagement';
import ErrorLogs from './components/admin/ErrorLogs';
import DatabaseManagement from './components/admin/DatabaseManagement';
import AssessmentReports from './components/admin/AssessmentReports';

// Route Protection
import AdminRoute from './components/routing/AdminRoute';

// Contexts
import AuthContext, { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { LayoutProvider } from './context/LayoutContext';
import { LanguageProvider } from './context/LanguageContext';
import { AccessibilityProvider } from './context/AccessibilityContext';

// Axios configuration is now imported from utils/axiosConfig.js

function App() {
  // These states are now managed in AuthContext
  const [hasOrganization, setHasOrganization] = useState(true);

  // Private route component
  const PrivateRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useContext(AuthContext);

    if (loading) return <div className="loading">Loading...</div>;

    // If not authenticated, redirect to login
    if (!isAuthenticated) return <Navigate to="/login" />;

    // If first login or no organization, redirect to organization setup
    if ((user && user.isFirstLogin) || !hasOrganization) return <Navigate to="/organization-setup" />;

    // Otherwise, render the children
    return children;
  };

 // Organization setup route component
  const OrganizationSetupRoute = () => {
    const { isAuthenticated, loading, user } = useContext(AuthContext);

    if (loading) return <div className="loading">Loading...</div>;

    // If not authenticated, redirect to login
    if (!isAuthenticated) return <Navigate to="/login" />;

    // If not first login and has organization, redirect to dashboard
    if (user && !user.isFirstLogin && hasOrganization) return <Navigate to="/" />;

    // Otherwise, render the organization setup
    return <OrganizationSetup />;
  };

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AccessibilityProvider>
          <LayoutProvider>
            <AuthProvider>
              <OrganizationProvider>
                <Router>
                  <AppContent 
                    hasOrganization={hasOrganization} 
                    PrivateRoute={PrivateRoute} 
                    OrganizationSetupRoute={OrganizationSetupRoute} 
                  />
                </Router>
              </OrganizationProvider>
            </AuthProvider>
          </LayoutProvider>
        </AccessibilityProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

// This component handles the main app content based on authentication state
const AppContent = ({ hasOrganization, PrivateRoute, OrganizationSetupRoute }) => {
  const { isAuthenticated, loading, user, needsPasswordChange, showTutorial, setShowTutorial } = useContext(AuthContext);

  // Check if user needs to see tutorial
  useEffect(() => {
    if (user && isAuthenticated && !user.isFirstLogin) {
      // Show tutorial if it's the user's first time after password change
      if (localStorage.getItem('showTutorial') === 'true') {
        setShowTutorial(true);
        localStorage.removeItem('showTutorial');
      }
    }
  }, [user, isAuthenticated, setShowTutorial]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }
  
  // If user needs to change password, show the ForcePasswordChange component
  if (isAuthenticated && needsPasswordChange) {
    return (
      <div className="app-container">
        {!loading && (
          <div className="app-wrapper">
            <Header />
            <main className="main-content">
              <FeedbackForm />
              <Routes>
                  <Route path="/login" element={!isAuthenticated ? <Login /> : needsPasswordChange ? <Navigate to="/force-password-change" /> : <Navigate to="/dashboard" />} />
                  <Route path="/register" element={!isAuthenticated ? <Register /> : needsPasswordChange ? <Navigate to="/force-password-change" /> : <Navigate to="/dashboard" />} />
                  <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
                  <Route path="/reset-password/:token" element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/dashboard" />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/accept-invite/:token" element={<AcceptInvite />} />
                  <Route path="/force-password-change" element={isAuthenticated && needsPasswordChange ? <ForcePasswordChange /> : <Navigate to="/dashboard" />} />
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                  <Route path="/assessments" element={<PrivateRoute><AssessmentList /></PrivateRoute>} />
                  <Route path="/assessments/:id" element={<PrivateRoute><AssessmentDetail /></PrivateRoute>} />
                  <Route path="/reports" element={<PrivateRoute><ReportList /></PrivateRoute>} />
                  <Route path="/reports/:id" element={<PrivateRoute><ReportDetail /></PrivateRoute>} />
                  <Route path="/maturity-scorecard" element={<PrivateRoute><MaturityScorecardPage /></PrivateRoute>} />
                  <Route path="/implementation-roadmap" element={<PrivateRoute><ImplementationRoadmap /></PrivateRoute>} />
                  <Route path="/scorecard/:id" element={<PrivateRoute><MaturityScorecardPage /></PrivateRoute>} />
                  <Route path="/maturity-scorecard/:id" element={<PrivateRoute><MaturityScorecardPage /></PrivateRoute>} />
                  <Route path="/scorecard-details" element={<PrivateRoute><ScorecardDetailsPage /></PrivateRoute>} />
                  <Route path="/scorecard-details/:id" element={<PrivateRoute><ScorecardDetailsPage /></PrivateRoute>} />
                  <Route path="/assessments/:id/scorecard-details" element={<PrivateRoute><ScorecardDetailsPage /></PrivateRoute>} />
                  <Route path="/findings-recommendations" element={<PrivateRoute><FindingsRecommendationsPage /></PrivateRoute>} />
                  <Route path="/findings-recommendations/:id" element={<PrivateRoute><FindingsRecommendationsPage /></PrivateRoute>} />
                  <Route path="/reports/industry" element={<PrivateRoute><IndustryAnalysisPage /></PrivateRoute>} />
                  <Route path="/account" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
                  <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                  <Route path="/organization-details" element={<PrivateRoute><OrganizationDetails /></PrivateRoute>} />
                  <Route path="/settings" element={<PrivateRoute><AppSettings /></PrivateRoute>} />
                  <Route path="/organization-setup" element={<OrganizationSetupRoute />} />
                <Route path="/force-password-change" element={isAuthenticated && needsPasswordChange ? <ForcePasswordChange /> : <Navigate to="/dashboard" />} />
                <Route path="*" element={<Navigate to="/force-password-change" />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
                <Route path="/admin/templates" element={<AdminRoute><TemplateManagement /></AdminRoute>} />
                <Route path="/admin/organizations" element={<AdminRoute><OrganizationManagement /></AdminRoute>} />
                <Route path="/admin/errors" element={<AdminRoute><ErrorLogs /></AdminRoute>} />
                <Route path="/admin/database" element={<AdminRoute><DatabaseManagement /></AdminRoute>} />
                <Route path="/admin/reports" element={<AdminRoute><AssessmentReports /></AdminRoute>} />
                <Route path="/admin/assessments/:id" element={<AdminRoute><AssessmentReports viewMode="details" /></AdminRoute>} />

                {/* Legal Routes - Now using modals instead */}

                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="app">
      {isAuthenticated && <Header />}
      {isAuthenticated && showTutorial && <AppTutorial />}
      <div className="main-container" style={{ display: 'flex', flexDirection: 'column', height: '100vh', marginTop: '60px' }}>
        <main className="content" style={{ flex: 1, padding: '20px' }}>
          <FeedbackForm />
          <Routes>
            <Route path="/login" element={!isAuthenticated ? <Login /> : needsPasswordChange ? <Navigate to="/force-password-change" /> : <Navigate to="/dashboard" />} />
            <Route path="/register" element={!isAuthenticated ? <Register /> : needsPasswordChange ? <Navigate to="/force-password-change" /> : <Navigate to="/dashboard" />} />
            <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/reset-password/:token" element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/force-password-change" element={isAuthenticated && needsPasswordChange ? <ForcePasswordChange /> : <Navigate to="/dashboard" />} />
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/assessments" element={<PrivateRoute><AssessmentList /></PrivateRoute>} />
            <Route path="/assessments/:id" element={<PrivateRoute><AssessmentDetail /></PrivateRoute>} />
            <Route path="/assessments/:id/edit" element={<PrivateRoute><AssessmentDetail editMode={true} /></PrivateRoute>} />
            <Route path="/reports" element={<PrivateRoute><ReportList /></PrivateRoute>} />
            <Route path="/reports/:id" element={<PrivateRoute><ReportDetail /></PrivateRoute>} />
            <Route path="/scorecard" element={<PrivateRoute><MaturityScorecardPage /></PrivateRoute>} />
            <Route path="/scorecard/:id" element={<PrivateRoute><MaturityScorecardPage /></PrivateRoute>} />
            <Route path="/maturity-scorecard" element={<PrivateRoute><MaturityScorecardPage /></PrivateRoute>} />
            <Route path="/maturity-scorecard/:id" element={<PrivateRoute><MaturityScorecardPage /></PrivateRoute>} />
            <Route path="/scorecard-details" element={<PrivateRoute><ScorecardDetailsPage /></PrivateRoute>} />
            <Route path="/scorecard-details/:id" element={<PrivateRoute><ScorecardDetailsPage /></PrivateRoute>} />
            <Route path="/assessments/:id/scorecard-details" element={<PrivateRoute><ScorecardDetailsPage /></PrivateRoute>} />
            <Route path="/findings-recommendations" element={<PrivateRoute><FindingsRecommendationsPage /></PrivateRoute>} />
            <Route path="/findings-recommendations/:id" element={<PrivateRoute><FindingsRecommendationsPage /></PrivateRoute>} />
            <Route path="/reports/industry" element={<PrivateRoute><IndustryAnalysisPage /></PrivateRoute>} />
            <Route path="/implementation-roadmap" element={<PrivateRoute><ImplementationRoadmap /></PrivateRoute>} />
            <Route path="/account" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/organization-details" element={<PrivateRoute><OrganizationDetails /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><AppSettings /></PrivateRoute>} />
            <Route path="/organization-setup" element={<OrganizationSetupRoute />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
            <Route path="/admin/templates" element={<AdminRoute><TemplateManagement /></AdminRoute>} />
            <Route path="/admin/organizations" element={<AdminRoute><OrganizationManagement /></AdminRoute>} />
            <Route path="/admin/errors" element={<AdminRoute><ErrorLogs /></AdminRoute>} />
            <Route path="/admin/database" element={<AdminRoute><DatabaseManagement /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><AssessmentReports /></AdminRoute>} />
            <Route path="/admin/assessments/:id" element={<AdminRoute><AssessmentReports viewMode="details" /></AdminRoute>} />

            {/* Legal Routes - Now using modals instead */}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default App;
